import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import ctaBg from "@/assets/cta-bg.webp";

export function SelectivePartnershipSection() {
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-10%", "12%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section
      ref={ref}
      className="relative isolate overflow-hidden py-28 md:py-36 bg-[#08080c] text-white"
    >
      <motion.div
        aria-hidden="true"
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-20 will-change-transform"
      >
        <img src={ctaBg} alt="" loading="lazy" className="h-full w-full object-cover opacity-60" />
      </motion.div>
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10"
        style={{
          background:
            "linear-gradient(180deg, rgba(8,8,12,0.85) 0%, rgba(8,8,12,0.65) 50%, rgba(8,8,12,0.92) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 opacity-[0.06] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
          backgroundSize: "180px 180px",
        }}
      />

      <div className="container mx-auto px-5 sm:px-8 lg:px-12 max-w-4xl relative z-10 text-center">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-xs font-semibold uppercase tracking-[0.28em] text-accent mb-6"
        >
          A boutique practice
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.21, 0.47, 0.32, 0.98] }}
          viewport={{ once: true, margin: "-80px" }}
          className="font-display font-bold text-white tracking-[-0.025em] leading-[1.05] text-3xl md:text-5xl lg:text-[3.5rem] mb-8 max-w-3xl mx-auto"
        >
          We partner selectively.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-white/75 text-lg md:text-xl leading-[1.7] max-w-2xl mx-auto mb-12"
        >
          We work closely with founder-led businesses where technology, AI, execution clarity, and long-term systems thinking are strategic priorities, not outsourced checklists.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <Link
            to="/contact"
            className="group inline-flex items-center gap-3 rounded-full bg-white text-[hsl(248_62%_21%)] pl-3 pr-7 py-2.5 font-medium shadow-[0_22px_50px_-15px_rgba(0,0,0,0.55)] hover:shadow-[0_28px_60px_-18px_rgba(0,0,0,0.7)] transition-all duration-300 hover:-translate-y-0.5"
          >
            <span className="grid place-items-center h-9 w-9 rounded-full bg-[hsl(248_62%_21%)] text-white transition-transform duration-300 group-hover:translate-x-0.5">
              <ArrowRight className="h-4 w-4" />
            </span>
            Request a Partnership Conversation
          </Link>
        </motion.div>
      </div>
    </section>
  );
}